import { useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  Button,
  Tooltip,
} from "@mui/material";
import {
  Close as CloseIcon,
  FactCheckOutlined as VerifyIcon,
} from "@mui/icons-material";
import { useJobDetailsContext } from "../../contexts/JobDetailsContext";
import { StatusBadge } from "./StatusBadge";
import { useAuth } from "../../contexts/AuthContext";
import { userCanAccessOps } from "../../utils/userPermissions";
import { BinderVerificationDrawer } from "../binder-verification/BinderVerificationDrawer";

/**
 * Job details header component that shows job information and status
 *
 * - Shows job client name, filing type, and year
 * - Displays job status badge
 * - Adapts to drawer/panel variant (hides close button in panel mode)
 */
export function JobDetailsHeader() {
  const { job, variant, onClose, context, opsActions } = useJobDetailsContext();
  const { user } = useAuth();
  const [verificationDrawerOpen, setVerificationDrawerOpen] = useState(false);

  const canVerifyBinders = userCanAccessOps(user);

  const verifiableArtifacts = (job?.processingResults ?? []).filter(
    (p) => p.artifactType === "binder" || p.artifactType === "supporting_doc",
  );
  const hasVerifiableArtifact = verifiableArtifacts.length > 0;

  const latestBinder = verifiableArtifacts
    .slice()
    .sort((a, b) => (a.uploadedAt < b.uploadedAt ? 1 : -1))[0];
  const pipelineStatus = latestBinder?.verification?.pipelineStatus;
  const isVerifying =
    pipelineStatus === "pending" || pipelineStatus === "processing";

  if (!job) return null;

  return (
    <Box sx={{ p: 2, borderBottom: 1, borderColor: "divider" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box>
          <Typography
            variant="h5"
            fontWeight={600}
            sx={{ color: "#082042", mb: 0.5 }}
          >
            {job.clientName || "Unknown Client"}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {job.filingType} {job.taxYear} •{" "}
            {job.clientNumber || job.jobId.substring(0, 8)}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {canVerifyBinders && (
            <Tooltip
              title={
                hasVerifiableArtifact
                  ? "View or run binder verification for this job"
                  : "No binder or tax-software import to verify"
              }
            >
              <span>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<VerifyIcon />}
                  disabled={!hasVerifiableArtifact}
                  onClick={() => setVerificationDrawerOpen(true)}
                >
                  {isVerifying ? "Verifying…" : "View Verification"}
                </Button>
              </span>
            </Tooltip>
          )}

          {/* Status Badge */}
          <StatusBadge
            status={job.status}
            size="medium"
            job={job}
            context={context}
            onStatusChange={
              context === "ops" ? opsActions.handleStatusChange : undefined
            }
          />

          {/* Only show close button in drawer variant, not in panel variant */}
          {variant !== "panel" && (
            <IconButton onClick={onClose} edge="end">
              <CloseIcon />
            </IconButton>
          )}
        </Box>
      </Box>
      <BinderVerificationDrawer
        firmId={job.firmId}
        jobId={verificationDrawerOpen ? job.jobId : null}
        onClose={() => setVerificationDrawerOpen(false)}
      />
    </Box>
  );
}
